import { useRef, useState } from 'react';

import FroalaEditor from 'react-froala-wysiwyg';
import FroalaEditorCore from 'froala-editor';

// Require Editor CSS files.
import 'froala-editor/css/froala_style.min.css';
import 'froala-editor/css/froala_editor.pkgd.min.css';

import 'froala-editor/js/plugins/word_paste.min.js';
import 'froala-editor/js/plugins/image.min.js';
import 'froala-editor/js/languages/de.js';
import 'froala-editor/js/plugins/table.min.js';
import 'froala-editor/js/plugins/emoticons.min.js';
import 'froala-editor/js/plugins/font_size.min.js';
import 'froala-editor/js/plugins/colors.min.js';

import { useField, useFormikContext } from 'formik';
import React from 'react';
import Config from 'Config';
import { useStore } from 'app/context';
import { AudioTranscriptionModal } from '../documents/AudioTranscriptionModal';

// Register a custom toolbar button that triggers an audio transcription and
// inserts the result at the current cursor position. Registration is global to
// Froala, so it must only happen once.
const TRANSCRIBE_COMMAND = 'transcribeAudio';
let transcribeCommandRegistered = false;
function registerTranscribeCommand() {
	if (transcribeCommandRegistered) {
		return;
	}
	transcribeCommandRegistered = true;
	const core = FroalaEditorCore as any;
	const micPath =
		'M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5-3c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z';
	core.DefineIconTemplate('dvsMic', '<svg viewBox="0 0 24 24" width="20" height="20" style="vertical-align:middle"><path d="[PATH]" fill="currentColor"></path></svg>');
	core.DefineIcon(TRANSCRIBE_COMMAND, { PATH: micPath, template: 'dvsMic' });
	core.RegisterCommand(TRANSCRIBE_COMMAND, {
		title: 'Sprachaufnahme',
		icon: TRANSCRIBE_COMMAND,
		undo: false,
		focus: false,
		callback: function (this: any) {
			// `this` is the Froala editor instance. Remember the cursor position so
			// the transcribed text can be inserted there once it is available.
			this.selection.save();
			if (this.dvsOnTranscribe) {
				this.dvsOnTranscribe();
			}
		},
	});
}

export const TextEditorField = ({ documentId, ...props }: { documentId?: string; name: string }) => {
	const { setFieldValue } = useFormikContext();
	const { uiStore } = useStore();
	const [field] = useField(props as any);
	const editorRef = useRef<TextEditor>(null);
	const insertModalId = uiStore.modalIds.audioRecordingInsert;

	// Add token to image URLs when displaying
	const addTokenToImageUrls = (html: string): string => {
		if (!html) return html;
		const token = localStorage.getItem('token');
		if (!token) return html;

		// Find all image URLs for this document and add token if not present
		const regex = new RegExp(`(${Config.API_BASE_URL}/api/documents/[^/]+/images/[^"?\\s]+)(?!\\?token)`, 'g');
		return html.replace(regex, `$1?token=${encodeURIComponent(token)}`);
	};

	// Remove token from image URLs before saving
	const removeTokenFromImageUrls = (html: string): string => {
		if (!html) return html;

		// Escape special regex characters in the base URL
		const escapedBaseUrl = Config.API_BASE_URL.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

		// Remove token query parameter from image URLs (keeping the base URL intact)
		const regex = new RegExp(`(${escapedBaseUrl}/api/documents/[^/]+/images/[^"?\\s]+)\\?token=[^"\\s&]+(&[^"\\s]*)?`, 'g');
		return html.replace(regex, '$1');
	};

	const [model] = useState<string>(addTokenToImageUrls(field.value as string));

	const onChange = (val: string) => {
		// Remove tokens from URLs before saving to avoid storing expired tokens
		const cleanedValue = removeTokenFromImageUrls(val);
		setFieldValue(props.name, cleanedValue);
	};

	const onTranscribeRequest = () => {
		uiStore.showModal(insertModalId);
	};

	const onInsertTranscription = (text: string) => {
		editorRef.current?.insertTranscribedText(text);
	};

	const onCancelTranscription = () => {
		editorRef.current?.cancelTranscribe();
	};

	return (
		<>
			<TextEditor ref={editorRef} text={model} onModelChange={onChange} documentId={documentId} onTranscribeRequest={Config.enableTranscriptions ? onTranscribeRequest : undefined} />
			{Config.enableTranscriptions && (
				<AudioTranscriptionModal modalId={insertModalId} doneLabel="Einfügen" onDone={onInsertTranscription} onCancel={onCancelTranscription} />
			)}
		</>
	);
};

interface IProps {
	text: string;
	onModelChange: (val: string) => void;
	documentId?: string;
	onTranscribeRequest?: () => void;
}
interface IState {
	text: string;
}

// froala does not like hooks
export class TextEditor extends React.Component<IProps, IState> {
	public state: IState = {
		text: this.props.text,
	};

	// Froala editor instance, captured on `initialized`.
	private editor: any = null;

	handleModelChange(model: string) {
		this.setState({
			text: model,
		});
		this.props.onModelChange(model);
	}

	// Insert transcribed text at the saved cursor position (or, if no cursor was
	// saved, wherever Froala currently points - typically the end of the text).
	insertTranscribedText(text: string) {
		const editor = this.editor;
		if (!editor || !text) {
			return;
		}
		const escaped = text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
		const html = '<p>' + escaped.replace(/\n+/g, '</p><p>') + '</p>';
		try {
			editor.selection.restore();
		} catch (e) {
			// no saved selection - fall through and insert at the current position
		}
		editor.html.insert(html);
		editor.undo.saveStep();
		this.handleModelChange(editor.html.get());
	}

	// Clear the saved selection markers when the user dismisses the recorder.
	cancelTranscribe() {
		if (this.editor) {
			try {
				this.editor.selection.restore();
			} catch (e) {
				// ignore - nothing to restore
			}
		}
	}

	render() {
		const { documentId, onTranscribeRequest } = this.props;

		const toolbarButtons: string[][] = [
			['bold', 'italic', 'underline', 'strikeThrough'],
			['fontFamily', 'fontSize', 'textColor', 'backgroundColor'],
			['insertTable'],
			['insertImage'], // Add image button to toolbar
			['inlineClass', 'inlineStyle', 'clearFormatting'],
			['undo', 'redo'],
		];
		if (onTranscribeRequest) {
			toolbarButtons.push([TRANSCRIBE_COMMAND]);
			registerTranscribeCommand();
		}

		// Base configuration
		const config: any = {
			language: 'de',
			toolbarButtons,
			tableResizerOffset: 10,
			tableResizingLimit: 50,
			wordPasteModal: true,
			attribution: false,
			key: 're1H1qC4C3B5D6D5C5hBi1a2d2Za1IXBh1f1THTBPLIIWBORpF1F1E1F4F1C11A7C2E5D3==',
		};

		const events: any = {};

		// Capture the editor instance and wire the transcription trigger.
		if (onTranscribeRequest) {
			const self = this;
			events['initialized'] = function (this: any) {
				self.editor = this;
				this.dvsOnTranscribe = onTranscribeRequest;
			};
		}

		// Configure image upload if document ID is provided
		if (documentId) {
			config.imageUpload = true;
			config.imageUploadURL = `${Config.API_BASE_URL}/api/documents/${documentId}/images`;
			config.imageUploadParam = 'file';
			config.imageUploadMethod = 'POST';
			config.imageMaxSize = 5 * 1024 * 1024; // 5MB
			config.imageAllowedTypes = ['jpeg', 'jpg', 'png', 'gif'];

			// Disable inserting images by URL - only allow upload
			config.imageInsertButtons = ['imageUpload'];

			// Handle image paste from clipboard
			config.imagePaste = true;
			config.imagePasteProcess = true;

			// Add authentication headers for upload
			const token = localStorage.getItem('token');
			config.requestHeaders = {
				'Authorization': 'Basic ' + window.btoa(Config.basicAuth),
				'x-auth-token': token || ''
			};

			// Also set headers for image requests (when Froala loads the image to display it)
			config.imageManagerLoadURL = `${Config.API_BASE_URL}/api/documents/${documentId}/images`;
			config.imageManagerLoadMethod = 'GET';
			config.imageManagerLoadParams = {};

			// Enable cross-domain requests with credentials
			config.requestWithCORS = true;
			config.requestWithCredentials = false;

			// Handle upload response
			Object.assign(events, {
				'image.uploaded': function(this: any, response: any) {
					// Handle string response (might be JSON string)
					if (typeof response === 'string') {
						try {
							response = JSON.parse(response);
						} catch (e) {
							console.error('Failed to parse image upload response:', e);
							return false;
						}
					}

					// Build the full URL with token
					let imageUrl = response.link;
					if (imageUrl && !imageUrl.startsWith('http')) {
						imageUrl = Config.API_BASE_URL + imageUrl;
					}

					const token = localStorage.getItem('token');
					if (imageUrl && token) {
						const separator = imageUrl.includes('?') ? '&' : '?';
						imageUrl = imageUrl + separator + 'token=' + encodeURIComponent(token);
					}

					// Manually insert the image with the correct URL
					if (this.image && imageUrl) {
						this.image.insert(imageUrl, false, null, this.image.get(), response);
						return false; // Prevent default behavior
					}

					return response;
				},
				'image.inserted': function($img: any) {
					// Remove the default width style that Froala adds
					$img.removeAttr('style');
					$img.css('width', '');
				},
				'image.error': function(error: any, response: any) {
					// Check if this is a loading error (code 1) - ignore these as upload succeeded
					if (error && error.code === 1) {
						return;
					}

					// Display error for actual upload failures
					let errorMessage = 'Fehler beim Hochladen des Bildes.';
					if (response) {
						try {
							if (typeof response === 'string') {
								errorMessage += ' ' + response;
							} else if (typeof response === 'object') {
								errorMessage += ' ' + JSON.stringify(response);
							}
						} catch (e) {
							console.error('Failed to parse error response:', e);
						}
					}
					alert(errorMessage);
				}
			});
		} else {
			// Disable image functionality if no document ID
			config.imagePaste = false;
			config.imageUpload = false;
		}

		config.events = events;

		return <FroalaEditor config={config} model={this.state.text} onModelChange={this.handleModelChange.bind(this)} />;
	}
}
